<template>
  <div class="wt-text-overlay-stage">
    <div v-show="gridMarkerStyle.show" id="to-grid-marker">
      <div id="to-grid-marker-top" :style="{ top: gridMarkerStyle.top }" />
      <div id="to-grid-marker-left" :style="{ left: gridMarkerStyle.left }" />
    </div>
    <!-- grids -->
    <now-this-grid />
    <general-grid />
    <!-- end grids -->
    <text-overlay
      v-for="(to, index) in textOverlays"
      :key="to.id"
      :textOverlay="to"
      :tabIndex="index"
      :maxZIndex="maxTextOverlayZindex"
      :isActive="to.id === activeTextOverlayId"
    />
  </div>
</template>

<script>
/** YY
 * Asset Editor's text overlay stage Vue (stage bar)
 */

import app from 'app';
import TextOverlay from 'AssetEditor/functionality/TextOverlay/TextOverlay.vue';
import NowThisGrid from 'AssetEditor/functionality/TextOverlay/grids/NowThisGrid.vue';
import GeneralGrid from 'AssetEditor/functionality/TextOverlay/grids/GeneralGrid.vue';
import PageMixin from 'AssetEditor/pages/PageMixin.vue';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import Analytics from 'c/WtAnalytics';
import 'webfont';
import { mapActions, mapGetters, mapMutations } from 'vuex';

function trackTextOverlay(e, obj) {
  Analytics.trigger('analytics:textOverlay:' + e, obj);
}

export default {
  mixins: [PageMixin],
  components: {
    'text-overlay': TextOverlay,
    'now-this-grid': NowThisGrid,
    'general-grid': GeneralGrid,
  },

  data: function () {
    return {
      activeTextOverlayId: null,
      disabledOverlays: true,
    };
  },

  computed: {
    ...mapGetters('assetEditor', [
      'isTempAgdDirty',
      'timeInAsset',
      'tmpAGD',
      'pageTmpState',
      'story',
      'serializedTextOverlays',
      'workModel',
    ]),

    totSelected: function () {
      return this.pageTmpState.totSelected;
    },
    textOverlays: function () {
      return this.tmpAGD.textOverlays;
    },
    maxTextOverlayZindex: function () {
      if (_.isEmpty(this.textOverlays)) {
        return 0;
      }
      return _.maxBy(this.textOverlays, 'zIndex').zIndex;
    },
    gridMarkerStyle: function () {
      if (this.pageTmpState.markerStyle) {
        // we need to reduce 1px because of the TO border widths
        return {
          top: this.pageTmpState.markerStyle.top + 'px',
          left: this.pageTmpState.markerStyle.left + 'px',
          show: this.pageTmpState.markerStyle.show,
        };
      } else {
        return {};
      }
    },
    activeTextOverlayInstance: function () {
      return this.getChildInstance(this.activeTextOverlayId);
    },
    textStyles: function () {
      return this.story.textStyles;
    },
  },

  beforeDestroy: function () {
    bus.$off('apply', this.apply);
    $('#assetEditor').off(this.eventPrefix);
    bus.$off('to:focusLast', this.focusLast);
    bus.$off('to:reset', this.resetOverlays);
    bus.$off('to:added', this.onAddTextOverlay);
    bus.$off('to:selected', this.onToSelected);
    bus.$off('to:editing', this.onToEditing);
    bus.$off('save-style', this.onSaveStyle);
    bus.$off('apply-style', this.applyStyle);
    bus.$off('apply-sub-style', this.applySubStyle);
    bus.$off('toggle-box-design', this.toggleBoxDesign);
    bus.$off('toggle-position', this.togglePosition);
  },

  created: function () {
    this.setTmpPageState({ mask: true });
    this.eventPrefix = '.textOverlayStage' + Date.now();
    $('#assetEditor').on(
      'mousedown' + this.eventPrefix,
      this.resetOverlays.bind(this)
    );
    bus.$on('apply', this.apply);
    bus.$on('to:focusLast', this.focusLast);
    bus.$on('to:reset', this.resetOverlays);
    bus.$on('to:added', this.onAddTextOverlay);
    bus.$on('to:selected', this.onToSelected);
    bus.$on('to:editing', this.onToEditing);
    bus.$on('save-style', this.onSaveStyle);
    bus.$on('apply-style', this.applyStyle);
    bus.$on('apply-sub-style', this.applySubStyle);
    bus.$on('toggle-box-design', this.toggleBoxDesign);
    bus.$on('toggle-position', this.togglePosition);
  },

  mounted: function () {
    this.track({ e: 'selectedTextOverlay' });
    this.focusLast();
  },

  watch: {
    totSelected: function (textOverlay) {
      if (textOverlay) {
        const child = this.getChildInstance(textOverlay.id);
        if (child) {
          child.mode = 'editing';
        }
        this.setTmpPageState({ totSelected: null });
      }
    },
    activeTextOverlayId: function () {
      bus.$emit('to:activeTextOverlayId', this.activeTextOverlayId);
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setTempTextOverlay',
      'setAGD',
      'deleteTempTextOverlay',
      'setTmpPageState',
      'addTextStyle',
      'updateTextStyle',
      'setLastTextOverlayEffect',
    ]),
    ...mapActions('assetEditor', ['changeCurrentPage', 'track']),

    setActiveTextOverlay: function (textOverlay) {
      this.activeTextOverlayId = textOverlay ? textOverlay.id : null;
      if (textOverlay) {
        window.activeTextOverlay = textOverlay;
        if (this.activeTextOverlayInstance) {
          this.activeTextOverlayInstance.syncBoxDesignCheckbox();
          this.activeTextOverlayInstance.syncPositionCheckbox();
        }
        bus.$emit('active-style', textOverlay.textStyleId);
        this.setTmpPageState({
          mask: false,
          textOverlay,
          lastActiveTo: this.pageTmpState.textOverlay,
        });
      } else {
        bus.$emit('active-style', 0);
        this.setTmpPageState({ mask: true, textOverlay });
        bus.$emit('reset-text-styles-box');
      }
    },
    getTextOverlayInstances: function () {
      return this.$children.filter((child) => {
        return _.isDefined(child.textOverlay);
      });
    },
    /**
     * getChildInstance by id and textOverlay as default prop key
     */
    getChildInstance: function (id, key = 'textOverlay') {
      return _.find(this.$children, (child) => {
        return child[key] && child[key].id === id;
      });
    },
    resetOverlays: function (e, id) {
      if (e) {
        const $target = $(e.target);
        const isResetDisabled =
          $target.closest('.js-disable-to-reset').length > 0;
        if (isResetDisabled) {
          return;
        }
        const $closestTo = $target.closest('.wt-text-overlay');
        const textOverlayClicked = $closestTo.length > 0;
        if (
          textOverlayClicked &&
          this.activeTextOverlayInstance &&
          this.activeTextOverlayInstance.$el === $closestTo[0]
        ) {
          return;
        }
      }
      let overlayFound = false;
      if (!_.isEmpty(this.textOverlays) && this.activeTextOverlayId !== id) {
        this.textOverlays.forEach((textOverlay) => {
          if (id && textOverlay.id === id) {
            this.setActiveTextOverlay(textOverlay);
            overlayFound = true;
            return;
          }
          const child = this.getChildInstance(textOverlay.id);
          if (child) {
            child.mode = 'view';
          }
        });
      }
      if (!overlayFound) {
        this.setActiveTextOverlay(null);
      }
    },
    focusLast: function () {
      // enter in edit mode of the last (in the set) TO view
      const visibleTextOverlays = _.filter(this.textOverlays, (textOverlay) => {
        return (
          textOverlay.animationStartTime <= this.timeInAsset &&
          textOverlay.animationEndTime >= this.timeInAsset
        );
      });
      const textOverlayOnFront = _.maxBy(visibleTextOverlays, 'zIndex');
      if (textOverlayOnFront) {
        this.activeTextOverlayId = textOverlayOnFront.id;
        const view = this.getChildInstance(textOverlayOnFront.id);
        if (view) {
          view.mode = 'editing';
        }
      }
    },

    apply: function () {
      this.getTextOverlayInstances().forEach((child) => {
        child.tokenizeHtml(true);
      });
      this.setAGD({
        textOverlays: this.serializedTextOverlays,
      });
      this.changeCurrentPage('preview');
    },

    backToPreview: function () {
      const cleanTo = (to) => {
        const clone = _.cloneDeepAndClean(to);
        delete clone.zIndex;
        delete clone._text;
        return clone;
      };
      const t1 = (this.tmpAGD.textOverlays || []).map((t) => cleanTo(t));
      const t2 = (this.workModel.agd.textOverlays || []).map((t) => cleanTo(t));

      this.askSaveChanges(!_.isEqual(t1, t2)).then(() => {
        this.setLastTextOverlayEffect(null);
      });
    },

    onToEditing: function (textOverlay) {
      if (textOverlay) {
        this.resetOverlays(null, textOverlay.id);
        this.$root.$emit('pause-video');
        this.setActiveTextOverlay(textOverlay);
      } else {
        this.focusLast();
      }
    },
    onToSelected: function (textOverlay) {
      this.resetOverlays(null, textOverlay.id);
      this.$root.$emit('pause-video');
      this.setActiveTextOverlay(textOverlay);
    },
    onAddTextOverlay: function (textOverlay) {
      this.$nextTick(() => {
        this.setActiveTextOverlay(textOverlay);
        const instance = this.getChildInstance(textOverlay.id);
        if (instance) {
          instance.mode = 'editing';
        }
      });
    },
    onSaveStyle: function ({ name, saveBoxDesign, savePosition, shareStyle }) {
      if (!this.activeTextOverlayInstance) {
        app.doctorController.report(
          'Tried to save text style with no active text overlay'
        );
        return;
      }
      const text = this.activeTextOverlayInstance.textOverlay._text;
      const shadowOffsetX =
        this.activeTextOverlayInstance.textOverlay.shadowOffsetX;
      const shadowOffsetY =
        this.activeTextOverlayInstance.textOverlay.shadowOffsetY;

      const style = this.activeTextOverlayInstance.getTextStyle();
      style.name = name;
      style.box.apply = style.box.applyCheckbox = saveBoxDesign;
      style.box.textShadowOffsetX = shadowOffsetX;
      style.box.textShadowOffsetY = shadowOffsetY;
      style.position.apply = style.position.applyCheckbox = savePosition;
      style.programmerId = this.story.programmer.id;
      style.privacy = shareStyle
        ? app.TEXT_STYLES.SHARED
        : app.TEXT_STYLES.PRIVATE;
      style.aspectRatio = this.story.aspectRatio.name;
      if (text === text.toUpperCase()) {
        style.box.allCaps = true;
      }
      style.id = Date.now();
      this.addTextStyle(style);
      trackTextOverlay('saveStyle', {
        boxSaved: saveBoxDesign,
        positionSaved: savePosition,
        privacy: style.privacy,
      });
      app.api.textStyles.post(_.omit(style, 'id')).done((response) => {
        const name = tk.context.user.firstName + ' ' + tk.context.user.lastName;
        this.updateTextStyle({
          id: style.id,
          newId: response.id,
          ownerId: tk.context.user.employeeId,
          ownerName: name.trim(),
          mode: 'applying',
        });
        this.lastTextStyle = _.extend({}, style);
        this.activeTextOverlayInstance.setTextStyleId(response.id);
        app.Vue.nextTick(() => {
          this.activeTextOverlayInstance.syncBoxDesignCheckbox(style);
          this.activeTextOverlayInstance.syncPositionCheckbox(style);
          bus.$emit('active-style', response.id);
        });
      });
      console.log(style);
    },
    applyStyle: function (style, activeSubStyle) {
      if (this.activeTextOverlayInstance) {
        this.activeTextOverlayInstance.mode = 'editing';
        this.activeTextOverlayInstance.applyStyle(style, activeSubStyle);
      }
    },
    applySubStyle: function (subStyle) {
      if (this.activeTextOverlayInstance) {
        this.activeTextOverlayInstance.applySubStyle(subStyle);
      }
    },
    toggleBoxDesign: function (style) {
      if (style) {
        trackTextOverlay('overrideBoxDesign', { checked: true });
        this.activeTextOverlayInstance.applyBoxDesign(style.box, style.size);
      } else {
        trackTextOverlay('overrideBoxDesign', { checked: false });
        this.activeTextOverlayInstance.revertBoxDesign();
      }
    },
    togglePosition: function (style) {
      if (style) {
        trackTextOverlay('overridePosition', { checked: true });
        this.activeTextOverlayInstance.applyPosition(
          style.position,
          style.size
        );
      } else {
        trackTextOverlay('overridePosition', { checked: false });
        this.activeTextOverlayInstance.revertPosition();
      }
    },
  },
};
</script>
